"use client";

import React, { createContext, useState, useContext, useCallback } from "react";

interface AnimationContextType {
  isTransitioning: boolean;
  triggerTransition: (callback: () => void) => void;
}

const AnimationContext = createContext<AnimationContextType | undefined>(
  undefined,
);

interface AnimationProviderProps {
  children: React.ReactNode;
}

export const AnimationProvider: React.FC<AnimationProviderProps> = ({
  children,
}) => {
  const [isTransitioning, setIsTransitioning] = useState(false);

  const triggerTransition = useCallback((callback: () => void) => {
    // Start the transition
    setIsTransitioning(true);

    // Wait a bit to allow the exit animation to play
    setTimeout(() => {
      // Execute the callback (like changing route)
      callback();

      // Reset transition state with a slight delay to ensure new content has rendered
      setTimeout(() => {
        setIsTransitioning(false);
      }, 50); // Short delay to prevent flickering
    }, 300); // Adjust based on your exit animation duration
  }, []);

  return (
    <AnimationContext.Provider value={{ isTransitioning, triggerTransition }}>
      {children}
    </AnimationContext.Provider>
  );
};

export const useAnimation = (): AnimationContextType => {
  const context = useContext(AnimationContext);
  if (!context) {
    throw new Error("useAnimation must be used within an AnimationProvider");
  }
  return context;
};
